/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type Message } from '../../../../../../contexts/AsideContext';
import { messages } from '../messages';
import { ChatMessage } from './ChatMessage';

const styles = cssMap({
	messagesContainer: {
		flexGrow: 1,
		overflowY: 'auto',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		textAlign: 'center',
		paddingInline: token('space.400'),
	},
});

type ChatMessageListProps = {
	chatMessages: Message[];
	assistantName?: string;
	onUseCypher?: (query: string) => void;
	isLoading: boolean;
	messagesEndRef: React.RefObject<HTMLDivElement>;
	testId?: string;
};

export const ChatMessageList = ({
	chatMessages,
	assistantName,
	onUseCypher,
	isLoading,
	messagesEndRef,
	testId = 'chat-message-list',
}: ChatMessageListProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.messagesContainer}
			role="region"
			aria-label={formatMessage(messages.chatConversationAriaLabel)}
			aria-live="polite"
			testId={testId}
		>
			{chatMessages.length === 0 ? (
				<Box xcss={styles.emptyState}>
					<Text color="color.text.subtlest">
						<FormattedMessage {...messages.emptyStateText} />
					</Text>
				</Box>
			) : (
				chatMessages.map((msg) => (
					<ChatMessage
						key={msg.id}
						messageRole={msg.role}
						content={msg.content}
						rawCypherQuery={msg.metadata?.rawCypherQuery}
						onUseCypher={onUseCypher}
						assistantName={assistantName ?? formatMessage(messages.assistantName)}
					/>
				))
			)}
			{isLoading && (
				<ChatMessage
					messageRole="assistant"
					content={formatMessage(messages.loadingText)}
					isLoading
					assistantName={assistantName ?? formatMessage(messages.assistantName)}
				/>
			)}
			<div ref={messagesEndRef} />
		</Box>
	);
};
